import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import type { Task } from '../../../types/home';
import { useUpdateTask } from '../../../hooks/useHome';
import { useToast } from '../../../hooks/useToast';
import css from '../HomePage.module.css';

interface Props {
  task: Task;
  onClose: () => void;
  onDelete: () => void;
}

export default function EditTaskModal({ task, onClose, onDelete }: Props) {
  const [desc, setDesc] = useState(task.Description ?? '');
  const [deadline, setDeadline] = useState('');
  const [status, setStatus] = useState(task.Status ?? 'In Progress');
  const updateTask = useUpdateTask();
  const { showToast } = useToast();

  useEffect(() => {
    if (task.Deadline) {
      // Convert to datetime-local format
      const d = new Date(task.Deadline);
      if (!isNaN(d.getTime())) {
        setDeadline(d.toISOString().slice(0, 16));
      }
    }
  }, [task.Deadline]);

  function handleSave() {
    if (!desc.trim() || !deadline || !status) {
      showToast('All fields are required.', 'warning');
      return;
    }

    updateTask.mutate(
      { taskID: task.TaskID, desc: desc.trim(), deadline, status },
      {
        onSuccess: () => {
          showToast('Task updated successfully', 'success');
          onClose();
        },
        onError: () => {
          showToast('Failed to update task', 'error');
        },
      },
    );
  }

  // Close on overlay click
  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div className={css.modalOverlay} onClick={handleOverlayClick}>
      <div className={css.modal}>
        <div className={css.modalHeader}>
          <button className={`${css.btnSm} ${css.btnDanger}`} onClick={onDelete}>
            Delete Task
          </button>
          <h4 className={css.modalTitle}>Edit Task</h4>
          <button className={css.modalClose} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={css.modalBody}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Task Description</label>
            <textarea
              className={css.formTextarea}
              rows={3}
              placeholder="Enter task description..."
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>

          <div className={css.formGroup}>
            <label className={css.formLabel}>Due Date (15-min intervals)</label>
            <input
              type="datetime-local"
              className={css.formInput}
              step={900}
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>

          <div className={css.formGroup}>
            <label className={css.formLabel}>Status</label>
            <select
              className={css.formSelect}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
        </div>

        <div className={css.modalFooter}>
          <button
            className={`${css.btnSm} ${css.btnPrimary}`}
            onClick={handleSave}
            disabled={updateTask.isPending}
          >
            {updateTask.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
          <button className={`${css.btnSm} ${css.btnOutline}`} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
